import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, Link } from 'react-router-dom'
import { Formik, Form } from 'formik'
import { login, clearError } from '../store/slices/authSlice'
import { Button, FormikInput, FormikPasswordInput, Alert } from '../components/ui'
import { loginSchema } from '../utils/validationSchemas'

const LoginPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isLoading, error, isAuthenticated } = useSelector((state) => state.auth)
  
  // Debug log in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('LoginPage render - error state:', error)
    }
  }, [error])

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  useEffect(() => {
    // Clear error when component mounts (only on initial mount)
    // Don't clear on every render to preserve errors from failed login attempts
  }, [])

  const handleSubmit = async (values, { setSubmitting }) => {
    // Clear any previous errors before attempting login
    dispatch(clearError())
    
    try {
      // Dispatch login action
      const result = await dispatch(login(values))
      
      // Handle the result
      if (login.fulfilled.match(result)) {
        // Login successful - navigate to dashboard
        navigate('/dashboard')
      } else if (login.rejected.match(result)) {
        // Login failed - error is already set in the reducer by the rejected case
        // The error will be displayed automatically via the error state
        setSubmitting(false)
      }
    } catch (err) {
      // Fallback error handling
      console.error('Unexpected error during login:', err)
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo/Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">LinkBoard</h1>
            <p className="text-gray-600">Sign in to your account</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4">
              <Alert variant="danger" onClose={() => dispatch(clearError())}>
                {error}
              </Alert>
            </div>
          )}
          
          {/* Debug: Show error state in development */}
          {process.env.NODE_ENV === 'development' && error && (
            <div className="mb-2 text-xs text-gray-500">
              Debug - Error: {JSON.stringify(error)}
            </div>
          )}

          {/* Login Form */}
          <Formik
            initialValues={{
              username: '',
              password: '',
            }}
            validationSchema={loginSchema}
            onSubmit={handleSubmit}
            enableReinitialize={false}
          >
            {({ isSubmitting }) => (
              <Form 
                className="space-y-6"
                noValidate
              >
                <FormikInput
                  id="username"
                  name="username"
                  label="Username or Email"
                  type="text"
                  placeholder="Enter your username or email"
                  disabled={isLoading || isSubmitting}
                />

                <FormikPasswordInput
                  id="password"
                  name="password"
                  label="Password"
                  placeholder="Enter your password"
                  disabled={isLoading || isSubmitting}
                />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={isLoading || isSubmitting}
              className="w-full"
            >
              Sign In
            </Button>
          </Form>
        )}
      </Formik>

      <div className="mt-6 text-center">
        <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700">
          Forgot your password?
        </Link>
      </div>
    </div>
  </div>
</div>
)
}

export default LoginPage

